/** Hooks pour les scénarios de travaux. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createScenario,
  deleteScenario,
  getApplicableImprovements,
  getBouquetsPreview,
  listScenarios,
  recalculateScenario,
  type CreateScenarioPayload,
} from "@/api/scenarios";

const SCENARIOS_KEY = (logementId: string) => ["scenarios", logementId] as const;
const APPLICABLE_IMPROVEMENTS_KEY = (logementId: string) =>
  ["applicable-improvements", logementId] as const;
const BOUQUETS_PREVIEW_KEY = (logementId: string) =>
  ["bouquets-preview", logementId] as const;

export function useScenarios(logementId: string) {
  return useQuery({
    queryKey: SCENARIOS_KEY(logementId),
    queryFn: () => listScenarios(logementId),
    enabled: !!logementId,
  });
}

/**
 * Pour chaque amélioration du catalog, indique si elle apporterait un
 * gain sur ce logement précis. Utilisé pour pré-filtrer l'affichage des
 * bouquets recommandés ("Installer une PAC air/eau" est masqué si le
 * logement a déjà une PAC géothermique).
 *
 * Le calcul backend simule chaque amélioration une à une → ~1-2 sec au
 * chargement. On garde le résultat en cache via React Query.
 */
export function useApplicableImprovements(logementId: string) {
  return useQuery({
    queryKey: APPLICABLE_IMPROVEMENTS_KEY(logementId),
    queryFn: () => getApplicableImprovements(logementId),
    enabled: !!logementId,
    staleTime: 5 * 60 * 1000,  // 5 min : payload du logement change rarement
  });
}

/**
 * Pour chaque bouquet (générique + recommandé par classe), simule le
 * résultat complet sur le logement précis : label atteint, ROI, relevance,
 * coût, aides. Permet d'afficher les badges "Recommandé" UNIQUEMENT si
 * ROI < 20 ans, au lieu de l'heuristique pré-V4 basée sur la classe DPE
 * seule (qui marquait tout "Recommandé" pour D/E sans vérifier la
 * rentabilité réelle).
 *
 * ~1.5 sec au chargement (6 simulations × ~250 ms), caché 5 min via
 * React Query.
 */
export function useBouquetsPreview(logementId: string) {
  return useQuery({
    queryKey: BOUQUETS_PREVIEW_KEY(logementId),
    queryFn: () => getBouquetsPreview(logementId),
    enabled: !!logementId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateScenario(logementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateScenarioPayload) => createScenario(logementId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: SCENARIOS_KEY(logementId) }),
  });
}

export function useDeleteScenario(logementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scenarioId: string) => deleteScenario(logementId, scenarioId),
    onSuccess: () => qc.invalidateQueries({ queryKey: SCENARIOS_KEY(logementId) }),
  });
}

export function useRecalculateScenario(logementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scenarioId: string) =>
      recalculateScenario(logementId, scenarioId),
    onSuccess: () => qc.invalidateQueries({ queryKey: SCENARIOS_KEY(logementId) }),
  });
}
